// What this browser keeps between page loads, one record per key.
//
// Every cache is named by a `CacheKey`: the server's origin, the account it
// was written for, the layout of its records (`schemaVersion`), and an
// `InputsHash` of what its values were worked out from (query texts, rule
// versions, the engine build), made by `hashInputs`. A key can only be made
// by `cacheKey`, which requires all four, so no cache can leave one out. A
// record is read back only under the same key.
//
// - A record says when the read that produced it began (`basis`), so a
//   write needs a `ReadTicket` from `begin()`, taken before that read. Reads
//   return each value with its basis, when it was written and its age, and
//   take a maximum age; a cache may set one for all its records.
// - Reads that fail are misses. Writes that fail are values (`WriteResult`),
//   never exceptions, and say whether storage was full; a write that
//   succeeded says whether it is durable (IndexedDB) or only in this page's
//   memory.
// - A tab holds a lease on the key it uses (`hold`). `gc` deletes the
//   account's records under any other key (another layout, or another
//   build's inputs) once nobody holds a lease on that key and they are
//   `keepOthersMs` old, and never a record of a newer layout.
// - An `InvalidationLog` holds, per account, what made kept data stale for a
//   day: the app's own writes to GitHub (an intent before the write, done
//   after), live notices, and pull requests opened. A cache given the log
//   drops a record whose read began at or before such an event for one of
//   its subjects, whether it happened here, in another tab (heard on a
//   BroadcastChannel, and stored again by every tab that hears it), or
//   before this page opened (read from storage on open). A cache whose
//   records are themselves what opening a pull request reads again (the
//   pull request page's last read) says the `opened` event does not drop
//   them: every other event does.

declare const hashBrand: unique symbol;

/** A hash of what cached values were worked out from; made only by `hashInputs`. */
export type InputsHash = string & { readonly [hashBrand]: true };

/**
 * The hash of `parts` (FNV-1a, 32 bits, hex). A cache whose values depend
 * on nothing beyond their own key passes `hashInputs()`, which says so.
 */
export function hashInputs(...parts: string[]): InputsHash {
  const s = parts.map((p) => `${p.length}:${p}`).join('\0');
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0') as InputsHash;
}

/** What names a cache: all four are required (see the file comment). */
export interface CacheKeyParts {
  origin: string;
  account: string;
  schemaVersion: number;
  inputsHash: InputsHash;
}

declare const made: unique symbol;

/** A `CacheKeyParts` checked by `cacheKey`; nothing else is one. */
export type CacheKey = Readonly<CacheKeyParts> & { readonly [made]: true };

const keys = new WeakSet<object>();

/** The key for these parts. Throws a RangeError on an empty part, a malformed hash, or a schema version that is not a whole number. */
export function cacheKey(parts: CacheKeyParts): CacheKey {
  for (const f of ['origin', 'account'] as const)
    if (typeof parts[f] !== 'string' || parts[f] === '') throw new RangeError(`cache key: ${f} must be a non-empty string`);
  if (typeof parts.inputsHash !== 'string' || !/^[0-9a-f]{8}$/.test(parts.inputsHash)) throw new RangeError('cache key: inputsHash must come from hashInputs');
  if (!Number.isInteger(parts.schemaVersion) || parts.schemaVersion < 0) throw new RangeError('cache key: schemaVersion must be a whole number');
  const k = Object.freeze({ origin: parts.origin, account: parts.account, schemaVersion: parts.schemaVersion, inputsHash: parts.inputsHash });
  keys.add(k);
  return k as CacheKey;
}

/** Why storage refused a write: full, not available (closed, deleted, another account now), or anything else. */
export interface StorageFailure {
  kind: 'quota' | 'unavailable' | 'other';
  message: string;
}

/** Whether a write was stored: `durable` when it outlives the page, false when it is only in the page's memory. */
export type WriteResult = { ok: true; durable: boolean } | { ok: false; failure: StorageFailure };

export function storageFailureOf(e: unknown): StorageFailure {
  const name = typeof e === 'object' && e !== null ? (e as { name?: unknown }).name : undefined;
  const message = typeof e === 'object' && e !== null && 'message' in e ? String((e as Error).message) : String(e);
  if (name === 'QuotaExceededError') return { kind: 'quota', message };
  if (name === 'InvalidStateError' || name === 'NotFoundError' || name === 'UnknownError') return { kind: 'unavailable', message };
  return { kind: 'other', message };
}

/** Key-value storage under string keys. */
export interface Backend {
  /** The value at `key`; undefined when there is none or it cannot be read. */
  get(key: string): Promise<unknown>;
  /** Every entry whose key starts with `prefix`; none when they cannot be read. */
  scan(prefix: string): Promise<[string, unknown][]>;
  /** Put each entry (an undefined value deletes its key), all or nothing. */
  write(entries: readonly (readonly [string, unknown])[]): Promise<WriteResult>;
}

/**
 * Storage in this page's memory. Its writes are not durable unless
 * `durable` (a stand-in for IndexedDB in tests). `quota` caps how many
 * entries it holds: a write past it fails as storage full would. Two
 * pages' caches given one share it, as two tabs share IndexedDB.
 */
export function memoryBackend(opts: { quota?: number; durable?: boolean } = {}): Backend {
  const m = new Map<string, unknown>();
  const durable = opts.durable ?? false;
  return {
    get: async (k) => structuredClone(m.get(k)),
    scan: async (prefix) =>
      [...m]
        .filter(([k]) => k.startsWith(prefix))
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([k, v]) => [k, structuredClone(v)]),
    write: async (entries) => {
      const next = new Map(m);
      for (const [k, v] of entries)
        if (v === undefined) next.delete(k);
        else next.set(k, structuredClone(v));
      if (opts.quota !== undefined && next.size > opts.quota && next.size > m.size)
        return { ok: false, failure: { kind: 'quota', message: 'memory quota exceeded' } };
      m.clear();
      for (const [k, v] of next) m.set(k, v);
      return { ok: true, durable };
    },
  };
}

/** `backend.write`, with a thrown error turned into a failure; nothing to write is a durable no-op. */
async function safeWrite(backend: Backend, entries: readonly (readonly [string, unknown])[]): Promise<WriteResult> {
  if (!entries.length) return { ok: true, durable: true };
  try {
    return await backend.write(entries);
  } catch (e) {
    return { ok: false, failure: storageFailureOf(e) };
  }
}

async function safeScan(backend: Backend, prefix: string): Promise<[string, unknown][]> {
  try {
    return await backend.scan(prefix);
  } catch {
    return [];
  }
}

const enc = encodeURIComponent;
const dec = decodeURIComponent;

const isObject = (x: unknown): x is Record<string, unknown> => typeof x === 'object' && x !== null && !Array.isArray(x);
const isTime = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

// ------------------------------------------------------------------ the log

/** Which kind of write the app made to a pull request: to GitHub, or its review marks to the changelist server. */
export type WriteKind = 'publish' | 'resolve' | 'review' | 'reply' | 'rerequest' | 'marks';

/**
 * What made kept data stale:
 * - `app`: the app's own write, recorded as an `intent` before it is sent
 *   and `done` once answered (or failed: it may have gone through), naming
 *   its intent. An intent with no `done` covers a window of `writeWindowMs`
 *   after it (the write may land on GitHub any time in it, even if the
 *   writing tab closed);
 * - `notice`: a live notice (someone else's change), by its type;
 * - `opened`: the viewer opened the pull request, so what it shows is read afresh.
 */
export type InvalidationSource =
  | { readonly source: 'app'; readonly kind: WriteKind; readonly phase: 'intent' }
  | { readonly source: 'app'; readonly kind: WriteKind; readonly phase: 'done'; readonly intent?: string }
  | { readonly source: 'notice'; readonly kind: string }
  | { readonly source: 'opened' }
  /**
   * Another build's event, of a source or kind this build does not know
   * (builds overlap during a deploy). The log makes it of what it reads or
   * hears, and never records it: whatever it was, its subject changed.
   */
  | { readonly source: 'unknown' };

/** One event for `subject` (a pull request's `prKeyOf` key) at `at` (epoch ms). */
export type Invalidation = InvalidationSource & { readonly id: string; readonly subject: string; readonly at: number };

/** How long the log keeps events. A cache record older than this is not vouched for. */
export const LOG_KEPT_MS = 24 * 60 * 60_000;

/** How long after an intent with no `done` its write may still land on GitHub. */
export const WRITE_WINDOW_MS = 2 * 60_000;

/** Opens the channel the log's tabs share; null where there is none. */
export type ChannelFactory = (name: string) => Pick<BroadcastChannel, 'postMessage' | 'close' | 'onmessage'> | null;

const broadcastChannel: ChannelFactory = (name) => (typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(name));

const KINDS: readonly string[] = ['publish', 'resolve', 'review', 'reply', 'rerequest', 'marks'] satisfies WriteKind[];

/** An event as stored or heard: of the log's shape, whether or not this build knows its source and kind. */
interface StoredEvent {
  id: string;
  subject: string;
  at: number;
}

const isEvent = (x: unknown): x is StoredEvent & Record<string, unknown> =>
  isObject(x) && typeof x.id === 'string' && typeof x.subject === 'string' && isTime(x.at) && typeof x.source === 'string';

/**
 * Whether this build knows what `x` says. An event it does not know (a
 * newer build's source or kind, open during a deploy) is kept as it was
 * written for the pages that do, and applied here by its subject alone,
 * as `unknown`: what is kept for the subject is dropped, which costs a
 * read at worst.
 */
function isInvalidation(x: StoredEvent & Record<string, unknown>): x is Invalidation & Record<string, unknown> {
  switch (x.source) {
    case 'app':
      return KINDS.includes(x.kind as string) && (x.phase === 'intent' || (x.phase === 'done' && (x.intent === undefined || typeof x.intent === 'string')));
    case 'notice':
      return typeof x.kind === 'string';
    case 'opened':
      return true;
    default:
      return false;
  }
}

/** The event to apply for `x`: itself when this build knows it, else an `unknown` change of its subject. */
const known = (x: StoredEvent & Record<string, unknown>): Invalidation =>
  isInvalidation(x) ? x : { id: x.id, subject: x.subject, at: x.at, source: 'unknown' };

/** An app write under way: `done` records that it was answered. */
export interface Intent {
  /** Whether the intent was stored. */
  stored: WriteResult;
  done(): Promise<WriteResult>;
}

/** The durable log of what made kept data stale, for one account at one origin (see the file comment). */
export class InvalidationLog {
  /** The latest event's time, by subject. */
  private latest = new Map<string, number>();
  /** The latest event's time other than an `opened`, by subject: what changed the subject, not only showed it. */
  private latestChange = new Map<string, number>();
  /** Events applied, by ID, with their time (pruned past the horizon). */
  private seen = new Map<string, number>();
  private listeners = new Set<(e: Invalidation) => void>();
  private channel: ReturnType<ChannelFactory>;
  private applied = 0;
  /** Intents with no `done` yet, by ID. */
  private open = new Map<string, { subject: string; at: number }>();
  /** Intents whose `done` was seen (it may arrive before them), by ID, with its time. */
  private closed = new Map<string, number>();

  private constructor(
    readonly origin: string,
    readonly account: string,
    private readonly prefix: string,
    private readonly backend: Backend,
    channelName: string,
    channels: ChannelFactory,
    private readonly now: () => number,
    readonly keepMs: number,
    readonly writeWindowMs: number,
  ) {
    this.channel = channels(channelName);
    if (this.channel)
      this.channel.onmessage = (e: MessageEvent<unknown>) => {
        if (!isEvent(e.data) || this.seen.has(e.data.id)) return;
        this.apply(known(e.data));
        // Every tab that hears it stores it too, as it came (known to this build or not): it outlives the tab that made it.
        void safeWrite(this.backend, [[this.keyOf(e.data), e.data]]);
      };
  }

  /**
   * The log for `account` at `origin`: it hears other tabs from now on,
   * then reads what is stored, deleting events older than `keepMs` (and
   * what is not an event at all).
   */
  static async open(opts: {
    origin: string;
    account: string;
    backend: Backend;
    channels?: ChannelFactory;
    now?: () => number;
    keepMs?: number;
    writeWindowMs?: number;
  }): Promise<InvalidationLog> {
    const scope = `${enc(opts.origin)}|${enc(opts.account)}|`;
    const log = new InvalidationLog(
      opts.origin,
      opts.account,
      `log|${scope}`,
      opts.backend,
      `changeling:invalidations|${scope}`,
      opts.channels ?? broadcastChannel,
      opts.now ?? Date.now,
      opts.keepMs ?? LOG_KEPT_MS,
      opts.writeWindowMs ?? WRITE_WINDOW_MS,
    );
    const doomed: [string, undefined][] = [];
    const horizon = log.now() - log.keepMs;
    for (const [k, v] of await safeScan(opts.backend, log.prefix)) {
      if (!isEvent(v) || v.at < horizon) doomed.push([k, undefined]);
      else log.apply(known(v));
    }
    await safeWrite(opts.backend, doomed);
    return log;
  }

  /**
   * Record that `subject` changed now, for `source`: applied here at once,
   * sent to the other tabs (which store it too), and stored. When the
   * result is not ok, or not durable, a page opened later may not know of
   * it: the caller should drop what it keeps for `subject`.
   */
  async record(source: InvalidationSource, subject: string): Promise<WriteResult> {
    return (await this.emit(source, subject)).stored;
  }

  private async emit(source: InvalidationSource, subject: string): Promise<{ id: string; stored: WriteResult }> {
    if (source.source === 'unknown') throw new TypeError('InvalidationLog: an unknown event is read, never recorded');
    const e = { ...source, id: crypto.randomUUID(), subject, at: this.now() } as Invalidation;
    this.apply(e);
    try {
      this.channel?.postMessage(e);
    } catch {
      /* closed: the other tabs read it from storage when they next open */
    }
    return { id: e.id, stored: await safeWrite(this.backend, [[this.keyOf(e), e]]) };
  }

  /**
   * Record an app write to `subject` before it is sent; call `done` once it
   * is answered. Stale data is dropped early (a re-read at worst), so a page
   * that closes before the write is answered loses nothing.
   */
  async intend(kind: WriteKind, subject: string): Promise<Intent> {
    const { id, stored } = await this.emit({ source: 'app', kind, phase: 'intent' }, subject);
    return { stored, done: () => this.record({ source: 'app', kind, phase: 'done', intent: id }, subject) };
  }

  /**
   * Whether `subject` changed at or after `at`, or a write to it begun
   * before `at` may still have been landing then (an intent with no `done`,
   * within `writeWindowMs`), or `at` is older than the log keeps. With
   * `opened: false`, the viewer opening the subject does not count: for
   * what that opening reads again itself.
   */
  changedSince(subject: string, at: number, opts: { opened?: boolean } = {}): boolean {
    if (at < this.now() - this.keepMs) return true;
    if (((opts.opened ?? true ? this.latest : this.latestChange).get(subject) ?? -Infinity) >= at) return true;
    for (const i of this.open.values()) if (i.subject === subject && i.at <= at && at < i.at + this.writeWindowMs) return true;
    return false;
  }

  /** Hear each event from now on, from this tab or another. */
  subscribe(listener: (e: Invalidation) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  close(): void {
    this.channel?.close();
    this.channel = null;
    this.listeners.clear();
  }

  /** How many event IDs it remembers (for tests of pruning). */
  get remembered(): number {
    return this.seen.size;
  }

  private keyOf(e: StoredEvent): string {
    return `${this.prefix}${e.at.toString(36).padStart(10, '0')}|${e.id}`;
  }

  private apply(e: Invalidation) {
    if (this.seen.has(e.id)) return;
    this.seen.set(e.id, e.at);
    this.latest.set(e.subject, Math.max(e.at, this.latest.get(e.subject) ?? -Infinity));
    if (e.source !== 'opened') this.latestChange.set(e.subject, Math.max(e.at, this.latestChange.get(e.subject) ?? -Infinity));
    if (e.source === 'app' && e.phase === 'intent' && !this.closed.has(e.id)) this.open.set(e.id, { subject: e.subject, at: e.at });
    if (e.source === 'app' && e.phase === 'done' && e.intent) {
      this.open.delete(e.intent);
      this.closed.set(e.intent, e.at);
    }
    for (const l of [...this.listeners]) l(e);
    if (++this.applied % 64 === 0) this.prune();
  }

  /** Forget what is past the horizon: `changedSince` answers true for it anyway. */
  private prune() {
    const horizon = this.now() - this.keepMs;
    for (const [id, at] of this.seen) if (at < horizon) this.seen.delete(id);
    for (const [s, at] of this.latest) if (at < horizon) this.latest.delete(s);
    for (const [s, at] of this.latestChange) if (at < horizon) this.latestChange.delete(s);
    for (const [id, i] of this.open) if (i.at + this.writeWindowMs < horizon) this.open.delete(id);
    for (const [id, at] of this.closed) if (at < horizon) this.closed.delete(id);
  }
}

// ---------------------------------------------------------------- the cache

declare const ticketBrand: unique symbol;

/** When a read began; taken from `Cache.begin` before reading, and required to store what it read. */
export type ReadTicket = { readonly basis: number } & { readonly [ticketBrand]: true };

/** A stored record: the value, when its read began (`basis`) and when it was written (`at`). */
interface StoredRecord {
  basis: number;
  at: number;
  value: unknown;
}

const isStored = (x: unknown): x is StoredRecord => isObject(x) && isTime(x.basis) && isTime(x.at) && 'value' in x;

/** A value read back, with when its read began, when it was written, how old it is, and until when the cache keeps it. */
export interface Entry<V> {
  value: V;
  basis: number;
  at: number;
  /** `now - basis`, ms. */
  age: number;
  /** When the cache's `maxAgeMs` expires it; null when it sets none. */
  expiresAt: number | null;
  /** The ticket it was stored under: stores it again, or what was worked out from it, as of the same read. */
  readonly ticket: ReadTicket;
}

/** One entry of `Cache.writeAll`: a value under the ticket of the read that produced it; a null value deletes the key. */
export interface Write<V> {
  key: string;
  value: V | null;
  ticket: ReadTicket;
}

type Invalidated<V> =
  | {
      /** The log whose events drop records; of the key's origin and account. */
      log: InvalidationLog;
      /** The subjects a record depends on: an event for any of them drops it. */
      subjects: (key: string, value: V) => readonly string[];
      /**
       * Whether the viewer opening a subject drops its records (true by
       * default). False for records that are themselves what the opened
       * page reads again, shown only until it has.
       */
      opened?: boolean;
    }
  | { log?: undefined; subjects?: undefined; opened?: undefined };

export type CacheOptions<V> = {
  /** Which cache, among those of one key. */
  name: string;
  key: CacheKey;
  /** The value in a stored record, or null when it is not one (it is then skipped). */
  decode: (raw: unknown) => V | null;
  backend: Backend;
  /** How long records under another key are kept once nobody holds it (default: a day). */
  keepOthersMs?: number;
  /** How old (by basis) a record may be before the cache drops it; none when unset. */
  maxAgeMs?: number;
  /** Records kept at most by `gc` (the least recently written go first). */
  limit?: number;
  /** How long a lease lasts without a heartbeat (default 5 min; heartbeats every third of it). */
  leaseMs?: number;
  now?: () => number;
} & Invalidated<V>;

export interface ReadOptions {
  /** Leave out records older than this (by basis); the cache's own `maxAgeMs` applies too. */
  maxAgeMs?: number;
}

export interface Cache<V> {
  readonly key: CacheKey;
  /** A ticket for a read beginning now; `write` stores what it read. */
  begin(): ReadTicket;
  /**
   * A ticket for a read that began at `basis` (epoch ms), a time the caller
   * took before that read: for answers kept in memory and stored later,
   * each under when it was worked out. A time past now counts as now.
   * Throws a RangeError for a time that is not finite.
   */
  ticketAt(basis: number): ReadTicket;
  get(key: string, opts?: ReadOptions): Promise<Entry<V> | undefined>;
  /** Every record that is still good, by key. */
  entries(opts?: ReadOptions): Promise<Map<string, Entry<V>>>;
  /**
   * Store each value read under `ticket` (null deletes its key); the rest
   * stay as they are. A value whose subjects changed since the ticket is
   * deleted instead: it is already stale.
   */
  write(changes: Record<string, V | null>, ticket: ReadTicket): Promise<WriteResult>;
  /**
   * `write` for values read at different times: each is stored under its
   * own ticket (or deleted, when its subjects changed since that ticket),
   * all in one write, all or nothing. Throws a TypeError for an entry
   * without a ticket.
   */
  writeAll(entries: readonly Write<V>[]): Promise<WriteResult>;
  /** Hold a lease on this cache's key, so other tabs' `gc` keeps its records; returns the release. */
  hold(): () => void;
  /** Delete what nobody needs any more (see the file comment). */
  gc(): Promise<WriteResult>;
}

const DAY = 24 * 60 * 60_000;
const LEASE_MS = 5 * 60_000;

/** A cache under `key` (see the file comment). Throws a TypeError for a key not made by `cacheKey`, or a log of another account. */
export function openCache<V>(opts: CacheOptions<V>): Cache<V> {
  const k = opts.key;
  if (!keys.has(k)) throw new TypeError('openCache: the key must come from cacheKey');
  if (opts.log && (opts.log.origin !== k.origin || opts.log.account !== k.account))
    throw new TypeError('openCache: the log is of another origin or account than the key');
  const now = opts.now ?? Date.now;
  const keepOthersMs = opts.keepOthersMs ?? DAY;
  const leaseMs = opts.leaseMs ?? LEASE_MS;
  const all = `cache|${enc(opts.name)}|${enc(k.origin)}|`;
  const own = `${all}${enc(k.account)}|`;
  const keyPart = `${k.schemaVersion}|${k.inputsHash}`;
  const mine = `${own}${keyPart}|`;
  const leases = `lease|${enc(opts.name)}|${enc(k.origin)}|${enc(k.account)}|`;

  const opened = { opened: opts.opened ?? true };
  const stale = (key: string, v: V, basis: number) => !!opts.log && opts.subjects(key, v).some((s) => opts.log.changedSince(s, basis, opened));

  /** The entry in `raw`, or null when it is malformed, stale or expired by the cache's policy. */
  const good = (key: string, raw: unknown, t: number): Entry<V> | null => {
    if (!isStored(raw)) return null;
    const v = opts.decode(raw.value);
    if (v === null || stale(key, v, raw.basis)) return null;
    const expiresAt = opts.maxAgeMs === undefined ? null : raw.basis + opts.maxAgeMs;
    if (expiresAt !== null && t >= expiresAt) return null;
    const basis = raw.basis;
    const entry = { value: v, basis, at: raw.at, age: t - basis, expiresAt } as Entry<V>;
    // An accessor that does not enumerate: an entry compares and copies as its data.
    return Object.defineProperty(entry, 'ticket', { get: () => ({ basis }) as ReadTicket });
  };

  const young = (e: Entry<V>, o: ReadOptions | undefined) => o?.maxAgeMs === undefined || e.age < o.maxAgeMs;

  /** Delete in the background; what could not be deleted goes on the next `gc`. */
  const collect = (doomed: string[]) => {
    if (doomed.length) void safeWrite(opts.backend, doomed.map((d) => [d, undefined] as const));
  };

  const writeAll = (entries: readonly Write<V>[]): Promise<WriteResult> => {
    for (const e of entries)
      if (!e.ticket || !isTime(e.ticket.basis)) throw new TypeError('Cache.write: the ticket of the read (begin()) is required');
    const at = now();
    return safeWrite(
      opts.backend,
      entries.map(
        ({ key, value: v, ticket }) =>
          [mine + enc(key), v === null || stale(key, v, ticket.basis) ? undefined : ({ basis: ticket.basis, at, value: v } satisfies StoredRecord)] as const,
      ),
    );
  };

  return {
    key: k,
    begin: () => ({ basis: now() }) as ReadTicket,
    ticketAt(basis) {
      if (!isTime(basis)) throw new RangeError('Cache.ticketAt: the time the read began is required');
      return { basis: Math.min(basis, now()) } as ReadTicket;
    },
    async get(key, o) {
      let raw: unknown;
      try {
        raw = await opts.backend.get(mine + enc(key));
      } catch {
        return undefined;
      }
      if (raw === undefined) return undefined;
      const e = good(key, raw, now());
      if (!e) collect([mine + enc(key)]);
      return e && young(e, o) ? e : undefined;
    },
    async entries(o) {
      const t = now();
      const out = new Map<string, Entry<V>>();
      const doomed: string[] = [];
      for (const [sk, raw] of await safeScan(opts.backend, mine)) {
        const key = dec(sk.slice(mine.length));
        const e = good(key, raw, t);
        if (!e) doomed.push(sk);
        else if (young(e, o)) out.set(key, e);
      }
      collect(doomed);
      return out;
    },
    write: (changes, ticket) => writeAll(Object.entries(changes).map(([key, value]) => ({ key, value, ticket }))),
    writeAll,
    hold() {
      const lease = `${leases}${keyPart}|${crypto.randomUUID()}`;
      const beat = () => void safeWrite(opts.backend, [[lease, { at: now() }]]);
      beat();
      const timer = setInterval(beat, leaseMs / 3);
      (timer as { unref?: () => void }).unref?.();
      return () => {
        clearInterval(timer);
        void safeWrite(opts.backend, [[lease, undefined]]);
      };
    },
    async gc() {
      const t = now();
      const doomed: string[] = [];
      // Keys someone holds: `<version>|<hash>`.
      const held = new Set<string>();
      for (const [lk, raw] of await safeScan(opts.backend, leases)) {
        const parts = lk.slice(leases.length).split('|');
        if (parts.length === 3 && isObject(raw) && isTime(raw.at) && t - raw.at < leaseMs) held.add(`${parts[0]}|${parts[1]}`);
        else doomed.push(lk);
      }
      const kept: [string, number][] = [];
      for (const [sk, raw] of await safeScan(opts.backend, own)) {
        const [version, hash, ...rest] = sk.slice(own.length).split('|');
        const v = Number(version);
        if (rest.length !== 1 || !Number.isInteger(v)) continue; // not a record this layout of key names
        const at = isStored(raw) ? raw.at : -Infinity;
        if (`${version}|${hash}` === keyPart) {
          if (good(dec(rest[0]), raw, t) === null) doomed.push(sk);
          else kept.push([sk, at]);
        } else if (v <= k.schemaVersion && !held.has(`${version}|${hash}`) && t - at >= keepOthersMs) doomed.push(sk);
      }
      if (opts.limit !== undefined && kept.length > opts.limit)
        for (const [sk] of kept.sort((a, b) => a[1] - b[1]).slice(0, kept.length - opts.limit)) doomed.push(sk);
      return safeWrite(opts.backend, doomed.map((d) => [d, undefined] as const));
    },
  };
}
